import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { FiDownload } from "react-icons/fi";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { baseURL } from "../../assets/constants/baseUrl";
import classes from "./style.module.css";

pdfjs.GlobalWorkerOptions.workerPort = new Worker(
  new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url),
  { type: "module" }
);

const normalizeFileName = (value = "") => {
  try {
    return decodeURIComponent(value).normalize("NFC");
  } catch {
    return value.normalize("NFC");
  }
};

const PdfViewer = () => {
  const { fileName } = useParams();
  const [searchParams] = useSearchParams();
  const pageParam = searchParams.get("page");
  const parsedPage = Number(pageParam);
  const requestedPage =
    /^\d+$/.test(pageParam || "") && Number.isSafeInteger(parsedPage) && parsedPage > 0
      ? parsedPage
      : 1;
  const viewerRef = useRef(null);
  const touchStartRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageWidth, setPageWidth] = useState(0);

  const fileUrl = useMemo(() => {
    const normalizedFileName = normalizeFileName(fileName);
    return `${baseURL}file/get/${encodeURIComponent(normalizedFileName)}`;
  }, [fileName]);

  useEffect(() => {
    setPageCount(0);
  }, [fileUrl]);

  useEffect(() => {
    if (pageCount > 0) {
      setCurrentPage(Math.min(requestedPage, pageCount));
    }
  }, [requestedPage, pageCount]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return undefined;

    const updatePageWidth = () => {
      setPageWidth(Math.min(viewer.clientWidth - 24, 900));
    };

    updatePageWidth();
    const resizeObserver = new ResizeObserver(updatePageWidth);
    resizeObserver.observe(viewer);

    return () => resizeObserver.disconnect();
  }, []);

  const handleTouchStart = (event) => {
    const touch = event.changedTouches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    if (!touchStartRef.current) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    if (deltaX < 0) {
      setCurrentPage((page) => Math.min(page + 1, pageCount));
    } else {
      setCurrentPage((page) => Math.max(page - 1, 1));
    }
  };

  return (
    <section
      className={classes.viewer}
      ref={viewerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        touchStartRef.current = null;
      }}
    >
      <Document
        key={fileUrl}
        file={fileUrl}
        onLoadSuccess={({ numPages }) => {
          setPageCount(numPages);
          setCurrentPage(Math.min(requestedPage, numPages));
        }}
        loading={<p className={classes.message}>در حال بارگذاری فایل...</p>}
        error={<p className={classes.error}>نمایش فایل PDF امکان‌پذیر نیست.</p>}
      >
        {pageWidth > 0 && pageCount > 0 && (
          <Page
            className={classes.page}
            pageNumber={currentPage}
            width={pageWidth}
          />
        )}
      </Document>
      {pageCount > 0 && (
        <nav className={classes.controls} aria-label="PDF page navigation">
          <button
            className={classes.navigationButton}
            type="button"
            onClick={() => setCurrentPage((page) => page + 1)}
            disabled={currentPage === pageCount}
            aria-label="Next page"
          >
            ‹
          </button>
          <span className={classes.pageNumber}>
            {currentPage} / {pageCount}
          </span>
          <button
            className={classes.navigationButton}
            type="button"
            onClick={() => setCurrentPage((page) => page - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            ›
          </button>
          <a
            className={classes.downloadButton}
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Open PDF in a new tab"
          >
            <FiDownload aria-hidden="true" />
            دانلود
          </a>
        </nav>
      )}
    </section>
  );
};

export default PdfViewer;
